import { Component, h, Host, Prop, Listen, AttachInternals } from '@stencil/core';
import { v4 as uuidv4 } from 'uuid';
import { IconName } from '../icon/icons';

export type ButtonType = 'button' | 'submit' | 'reset';

export type IconPosition = 'none' | 'right' | 'left';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';

export type GapSize = 'small' | 'big';

@Component({
  tag: 'anchor-button',
  styleUrl: 'button.scss',
  shadow: true,
  formAssociated: true,
})
export class Button {
  @AttachInternals() internals: ElementInternals;

  /**
   *  Give this instance of button a custom ID, or keep the default.
   */
  @Prop({}) uuid: string = uuidv4();

  /**
   *  Set a text to be displayed in the content slot of the button
   */
  @Prop() text: string;

  /**
   *  Set the variant of the button
   */
  @Prop() variant?: ButtonVariant = 'primary';

  /**
   * Set to true if the Button is displayed on dark background
   */
  @Prop({ mutable: true }) onDark?: boolean = false;

  /**
   *  Set an icon to be displayed on the button
   */
  @Prop() icon?: IconName;

  /**
   *  Set the type of button
   */
  @Prop() type?: ButtonType = 'button';

  /**
   *  Set the position of the icon on a button with an icon
   */
  @Prop() iconPlacement?: IconPosition = 'none';

  /**
   *  Set whether or not the button should be disabled
   *
   * **NB!** This should be rarely be used as it's a potential accessibility issue. Disabled buttons are ignored by screen readers and keyboard navigation.
   */
  @Prop({ mutable: true, reflect: true }) disabled?: boolean = false;

  /**
   * Set the Button state to loading to display a spinner icon
   */
  @Prop({ mutable: true, reflect: true }) loading: boolean = false;

  @Listen('click')
  handleClick() {
    if (this.type === 'submit') {
      this.internals.form.requestSubmit();
    }
  }

  render() {
    const buttonClass = `
      anchor-c-btn 
      anchor-c-btn--${this.variant}${this.onDark ? '--on-dark' : ''}
      ${this.iconPlacement === 'left' ? 'anchor-c-btn--icon-left' : ''} 
      ${this.loading ? 'loading' : ''}
      `;
    return (
      <Host style={{ pointerEvents: this.disabled ? 'none' : undefined }}>
        <button id={this.uuid} class={buttonClass} disabled={this.disabled} type={this.type}>
          {this.loading && <anchor-spinner class="anchor-c-btn__spinner"></anchor-spinner>}
          <span class="anchor-c-btn__text">{this.text}</span>
          {this.icon && <anchor-icon name={this.icon} size={24} stroke={1.5} class="anchor-c-btn__icon"></anchor-icon>}
        </button>
      </Host>
    );
  }
}
